import React, { ReactNode } from "react";
import { components } from "@api/schema";
import { IAppCurrency } from "@App/types";
import { Money } from "@Shared/Components/Money/Money";
import { FormattedTimeUnit } from "@I18n/Components/FormattedTimeUnit/FormattedTimeUnit";

type TRateType = components["schemas"]["serviceOffer"]["rateType"];
type TBillingUnit = components["schemas"]["serviceOffer"]["billingUnit"];

interface IUseLabelProps {
  priceCents: number;
  rateType: TRateType;
  billingUnit: TBillingUnit;
  currency: IAppCurrency;
  expensesFeesCents?: number;
  feePercentage?: number;
}

interface IUseLabelResult {
  amountLabel: ReactNode;
}

export const useSubscriptionDiscountLabel = ({
  priceCents,
  rateType,
  billingUnit,
  currency,
  expensesFeesCents,
  feePercentage,
}: IUseLabelProps): IUseLabelResult => {
  if (rateType === "estimate") {
    return { amountLabel: feePercentage !== undefined ? `${feePercentage}%` : null };
  }

  const showUnit = rateType !== "flat" && billingUnit !== "flat";

  const ratePart = (
    <>
      <Money cents={priceCents} currency={currency} />
      {showUnit && (
        <> / <FormattedTimeUnit unit={billingUnit} format="short" /></>
      )}
    </>
  );

  const isFlatRate = billingUnit === "flat";
  const amountLabel: ReactNode =
    expensesFeesCents !== undefined && expensesFeesCents > 0 ? (
      isFlatRate ? (
        <Money cents={priceCents + expensesFeesCents} currency={currency} />
      ) : (
        <>
          {ratePart} + <Money cents={expensesFeesCents} currency={currency} />
        </>
      )
    ) : (
      ratePart
    );

  return { amountLabel };
};
